import { createI18n } from 'vue-i18n';
import elementZhCn from 'element-plus/es/locale/lang/zh-cn';
import elementKo from 'element-plus/es/locale/lang/ko';
import type { Language } from 'element-plus/es/locale';
import { APP_LOCALES, DEFAULT_LOCALE, type AppLocale } from '@dsv/shared';
import zhCN from './locales/zh-CN';
import koKR from './locales/ko-KR';
import dsZhCN from './locales/design-sheet.zh-CN';
import dsKoKR from './locales/design-sheet.ko-KR';
import uxZhCN from './locales/experience.zh-CN';
import uxKoKR from './locales/experience.ko-KR';
import responsibilityZh from './locales/responsibility.zh-CN';
import responsibilityKo from './locales/responsibility.ko-KR';
import intakeZh from './locales/intake.zh-CN';
import intakeKo from './locales/intake.ko-KR';
import plannerZh from './locales/planner-style.zh-CN';
import plannerKo from './locales/planner-style.ko-KR';
import ndZh from './locales/nd-readiness.zh-CN';
import ndKo from './locales/nd-readiness.ko-KR';
import factoryZh from './locales/factory-collaboration.zh-CN';
import factoryKo from './locales/factory-collaboration.ko-KR';
import designerZh from './locales/designer-draft.zh-CN';
import designerKo from './locales/designer-draft.ko-KR';

const LOCALE_STORAGE_KEY = 'dsv.locale';

/**
 * 界面语言。设计系统原本用「同一处文案中韩双写 + CSS 显隐」的方式，
 * 本项目改为 vue-i18n 运行时切换 —— Element Plus 自身也需要按语言换语言包，
 * 两套机制并行会失控。字体仍沿用设计系统：body 上挂 lang-cn / lang-kr 切字体族。
 */
export const ELEMENT_LOCALES: Record<AppLocale, Language> = {
  'zh-CN': elementZhCn,
  'ko-KR': elementKo,
};

export const LOCALE_OPTIONS: { value: AppLocale; label: string }[] = [
  { value: 'zh-CN', label: '中文' },
  { value: 'ko-KR', label: '한국어' },
];

function detectLocale(): AppLocale {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored && (APP_LOCALES as readonly string[]).includes(stored)) {
    return stored as AppLocale;
  }
  return navigator.language.startsWith('ko') ? 'ko-KR' : DEFAULT_LOCALE;
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  // 按模块拆分语言包后在这里合并，避免单个语言文件无限膨胀
  messages: {
    'zh-CN': { ...zhCN, ...dsZhCN, ...uxZhCN, ...responsibilityZh, ...intakeZh, ...plannerZh, ...ndZh, ...factoryZh, ...designerZh },
    'ko-KR': { ...koKR, ...dsKoKR, ...uxKoKR, ...responsibilityKo, ...intakeKo, ...plannerKo, ...ndKo, ...factoryKo, ...designerKo },
  },
});

/** 切换语言：同时更新 vue-i18n、html lang、body 字体类名并持久化 */
export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale;
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  document.documentElement.lang = locale;
  document.body.classList.toggle('lang-kr', locale === 'ko-KR');
  document.body.classList.toggle('lang-cn', locale === 'zh-CN');
}

export function currentLocale(): AppLocale {
  return i18n.global.locale.value as AppLocale;
}

/** 在组件外（如 axios 拦截器）翻译文案 */
export function t(key: string, named?: Record<string, unknown>): string {
  return named
    ? (i18n.global.t as (k: string, n: Record<string, unknown>) => string)(key, named)
    : (i18n.global.t as (k: string) => string)(key);
}
